var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo } from 'react';
import { getSmoothStepPath, BaseEdge } from '@xyflow/react';
// Custom Mind Map Edge Component
var MindMapEdge = function (_a) {
    var id = _a.id, sourceX = _a.sourceX, sourceY = _a.sourceY, targetX = _a.targetX, targetY = _a.targetY, sourcePosition = _a.sourcePosition, targetPosition = _a.targetPosition, data = _a.data, selected = _a.selected;
    // Type assertion for data
    var edgeData = data;
    // Calculate the smooth step path
    var _b = getSmoothStepPath({
        sourceX: sourceX,
        sourceY: sourceY,
        sourcePosition: sourcePosition,
        targetX: targetX,
        targetY: targetY,
        targetPosition: targetPosition,
        borderRadius: 20,
    }), edgePath = _b[0], labelX = _b[1], labelY = _b[2];
    // Edge styling based on state
    var strokeColor = selected
        ? '#000000'
        : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted)
            ? '#3B82F6'
            : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isSearchHighlight)
                ? '#fbbf24'
                : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.color) || '#9CA3AF';
    var strokeWidth = selected
        ? 4
        : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted)
            ? 3
            : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isSearchHighlight)
                ? 3
                : 2;
    var opacity = selected
        ? 0.9
        : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted)
            ? 0.8
            : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isSearchHighlight)
                ? 0.8
                : 0.6;
    return (_jsxs(_Fragment, { children: [_jsx(BaseEdge, { path: edgePath, style: {
                    stroke: 'rgba(0,0,0,0.1)',
                    strokeWidth: strokeWidth + 1,
                    transform: 'translate(2px, 2px)',
                } }), _jsx(BaseEdge, { id: id, path: edgePath, style: {
                    stroke: strokeColor,
                    strokeWidth: strokeWidth,
                    opacity: opacity,
                    strokeLinecap: 'round',
                }, className: "transition-all duration-300" }), (selected || (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted) || (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isSearchHighlight)) && (_jsx(BaseEdge, { path: edgePath, style: {
                    stroke: strokeColor,
                    strokeWidth: 2,
                    opacity: 0.4,
                    strokeDasharray: '8,8',
                    animation: 'dash 1s linear infinite',
                } })), selected && (edgeData === null || edgeData === void 0 ? void 0 : edgeData.connectionType) && (_jsxs("g", { children: [_jsx("circle", { cx: labelX, cy: labelY, r: 12, fill: "white", stroke: strokeColor, strokeWidth: 2, opacity: 0.9, className: "drop-shadow-sm" }), _jsx("text", __assign({ x: labelX, y: labelY, textAnchor: "middle", dominantBaseline: "middle", fontSize: 10, fill: strokeColor, className: "pointer-events-none select-none font-semibold" }, { children: edgeData.connectionType.charAt(0).toUpperCase() }))] })), (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isBidirectional) && (_jsxs("g", { children: [_jsxs("defs", { children: [_jsx("marker", __assign({ id: "arrowhead-".concat(id), markerWidth: "10", markerHeight: "7", refX: "9", refY: "3.5", orient: "auto" }, { children: _jsx("polygon", { points: "0 0, 10 3.5, 0 7", fill: strokeColor }) })), _jsx("marker", __assign({ id: "arrowhead-reverse-".concat(id), markerWidth: "10", markerHeight: "7", refX: "1", refY: "3.5", orient: "auto-start-reverse" }, { children: _jsx("polygon", { points: "10 0, 0 3.5, 10 7", fill: strokeColor }) }))] }), _jsx(BaseEdge, { path: edgePath, style: {
                            stroke: 'transparent',
                            strokeWidth: 1,
                            markerStart: "url(#arrowhead-reverse-".concat(id, ")"),
                            markerEnd: "url(#arrowhead-".concat(id, ")"),
                        } })] }))] }));
};
// Curved Edge - For organic, flowing connections
export var CurvedMindMapEdge = memo(MindMapEdge);
// Straight Edge - For direct, logical connections
export var StraightMindMapEdge = memo(function (props) {
    var edgeData = props.data;
    var strokeColor = props.selected
        ? '#000000'
        : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted)
            ? '#3B82F6'
            : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isSearchHighlight)
                ? '#fbbf24'
                : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.color) || '#9CA3AF';
    var strokeWidth = props.selected ? 4 : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted) ? 3 : 2;
    var opacity = props.selected ? 0.9 : (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted) ? 0.8 : 0.6;
    return (_jsxs(_Fragment, { children: [_jsx("line", { x1: props.sourceX + 2, y1: props.sourceY + 2, x2: props.targetX + 2, y2: props.targetY + 2, stroke: "rgba(0,0,0,0.1)", strokeWidth: strokeWidth + 1 }), _jsx("line", { x1: props.sourceX, y1: props.sourceY, x2: props.targetX, y2: props.targetY, stroke: strokeColor, strokeWidth: strokeWidth, opacity: opacity, strokeLinecap: "round", className: "transition-all duration-300" }), (props.selected || (edgeData === null || edgeData === void 0 ? void 0 : edgeData.isHighlighted)) && (_jsx("line", { x1: props.sourceX, y1: props.sourceY, x2: props.targetX, y2: props.targetY, stroke: strokeColor, strokeWidth: 2, opacity: 0.4, strokeDasharray: "8,8", style: { animation: 'dash 1s linear infinite' } }))] }));
});
// Export main edge component
export { MindMapEdge };
export default MindMapEdge;
